import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ChartConfiguration, ChartData } from 'chart.js';
import { Subscription } from 'rxjs';
import { UserSessionService } from '../../services/user-session.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { UserService } from '../../services/user.service';
import {
  ControlinventoryService,
  ControlInventoryAsset,
  ControlInventoryAssetUser,
  ControlInventoryAssetListResponse,
  ControlInventoryCatalogItem,
  ControlInventoryDashboard,
  ControlInventoryHistoryItem,
  ControlInventoryKit
} from '../../services/controlinventory.service';

type InventoryTab = 'dashboard' | 'assets' | 'kits';
type InventoryAssetStatus = 'ALL' | 'ACTIVE' | 'STOCK' | 'BROKEN';
type InventoryCatalogTab = 'TYPE' | 'CATEGORY' | 'SUBCATEGORY' | 'BRAND' | 'LOCATION';
type InventoryAssetModalTab = 'details' | 'link' | 'history';
type InventoryAssetNature = '' | 'PHYSICAL' | 'VIRTUAL' | 'CLOUD_SERVICE';

interface InventoryTenantUser {
  id: number;
  name: string;
  email?: string | null;
  active?: number | boolean | null;
}

@Component({
  selector: 'app-controlinventory',
  templateUrl: './controlinventory.component.html',
  styleUrls: ['./controlinventory.component.scss']
})
export class ControlinventoryComponent implements OnInit, OnDestroy {
  @ViewChild('photoFileInput') photoFileInput?: ElementRef<HTMLInputElement>;

  tenantId: number | null = null;
  userId: number | null = null;
  activeMainTab: InventoryTab = 'dashboard';
  loading = false;
  loadingDashboard = false;
  savingAsset = false;
  savingKit = false;
  savingCatalog = false;
  showCatalogModal = false;
  dashboardLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: { duration: 420 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#7f92aa',
          font: { size: 12, weight: 'bold' },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e7eef8' },
        ticks: {
          color: '#7f92aa',
          font: { size: 11, weight: 600 },
          precision: 0
        }
      }
    }
  };
  readonly dashboardPeriodOptions = [6, 12, 24];
  dashboardPeriodMonths = 12;

  dashboard: ControlInventoryDashboard = {
    active_assets: 0,
    inactive_assets: 0,
    kits: 0,
    damaged_assets: 0,
    assets_with_kit: 0,
    assets_without_kit: 0,
    by_type: [],
    by_status: [],
    by_category: [],
    by_brand: [],
    by_location: [],
    by_department: [],
    by_kit: [],
    by_created_by: [],
    by_updated_by: [],
    recent_history: []
  };
  dashboardAssets: ControlInventoryAsset[] = [];
  assets: ControlInventoryAsset[] = [];
  assetsTotal = 0;
  kits: ControlInventoryKit[] = [];
  history: ControlInventoryHistoryItem[] = [];
  catalogItems: ControlInventoryCatalogItem[] = [];
  currentUser: any = null;

  assetSearch = '';
  assetSkuFilter = '';
  assetTypeFilter = 'ALL';
  assetStatusFilter: InventoryAssetStatus = 'ACTIVE';
  kitSearch = '';
  assetPageSize = 10;
  assetPageIndex = 0;

  showAssetModal = false;
  showKitModal = false;
  editingAssetId: number | null = null;
  editingKitId: number | null = null;
  editingCatalogId: number | null = null;
  assetModalTab: InventoryAssetModalTab = 'details';
  assetTenantUsers: InventoryTenantUser[] = [];
  assetLinkedUserIds: number[] = [];
  assetHistoryItems: ControlInventoryHistoryItem[] = [];
  assetUserSearch = '';
  assetUsersLoading = false;
  assetHistoryLoading = false;
  selectedCatalogTab: InventoryCatalogTab = 'TYPE';
  photoPreviewUrl: string | null = null;
  photoDragActive = false;
  assetSaveError: string | null = null;
  catalogSaveError: string | null = null;
  showDashboardFilterModal = false;
  departments: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string | null }> = [];
  historyPageSize = 5;
  historyMaxRecords = 50;
  historyPageIndex = 0;
  dashboardFilters = {
    type: 'ALL',
    status: 'ALL',
    category: 'ALL',
    location: 'ALL',
    created_by: 'ALL',
    department_id: 'ALL'
  };
  dashboardActiveFiltersCount = 0;
  dashboardFilterSummary: string[] = ['Sem filtros aplicados'];
  dashboardTotalAssets = 0;
  dashboardActiveRate = 0;
  dashboardKitCoverageRate = 0;
  dashboardDamageRate = 0;
  activeAssetsCount = 0;
  inactiveAssetsCount = 0;
  kitsCount = 0;
  damagedAssetsCount = 0;
  assetsWithKitCount = 0;
  assetsWithoutKitCount = 0;
  dashboardTopType = 'Sem dados';
  dashboardTopCategory = 'Sem dados';
  dashboardTopStatus = 'Sem dados';
  dashboardTopCreator = 'Sem dados';
  dashboardTopDepartment = 'Sem dados';
  private monthlyRegistrationsChartCacheSignature = '';
  private monthlyRegistrationsChartCache: ChartData<'line'> = { labels: [], datasets: [] };
  departmentDonutSegments: Array<{ label: string; count: number; percent: number; color: string }> = [];
  departmentDonutGradient = 'conic-gradient(#e5ebf6 0deg 360deg)';
  departmentDonutTotal = 0;
  categoryDonutSegments: Array<{ label: string; count: number; percent: number; color: string }> = [];
  categoryDonutGradient = 'conic-gradient(#e5ebf6 0deg 360deg)';
  categoryDonutTotal = 0;
  currentCatalogItems: ControlInventoryCatalogItem[] = [];
  catalogCategoryOptions: ControlInventoryCatalogItem[] = [];
  assetDepartmentOptions: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string | null }> = [];
  assetTypeBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetStatusBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetCategoryBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetBrandBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetLocationBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetDepartmentBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetKitBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetCreatedByBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  assetUpdatedByBars: Array<{ label: string; count: number; percent: number; color: string }> = [];
  dashboardCreatorOptions: Array<{ value: string; label: string }> = [];
  dashboardUpdaterOptions: Array<{ value: string; label: string }> = [];
  dashboardDepartmentFilterOptions: Array<{ value: string; label: string }> = [];
  dashboardTypeOptions: Array<{ value: string; label: string }> = [];
  dashboardStatusOptions: Array<{ value: string; label: string }> = [
    { value: 'ALL', label: 'Todos os status' },
    { value: 'ACTIVE', label: 'Em uso' },
    { value: 'STOCK', label: 'Em estoque' },
    { value: 'BROKEN', label: 'Quebrado' }
  ];
  dashboardCategoryOptions: Array<{ value: string; label: string }> = [];
  dashboardLocationOptions: Array<{ value: string; label: string }> = [];
  dashboardKitOptions: Array<{ value: string; label: string }> = [];
  pagedHistory: ControlInventoryHistoryItem[] = [];
  historyPageCount = 1;
  historyPageStart = 0;
  historyPageEnd = 0;
  private assetUsersLoadedTenantId: number | null = null;
  private assetFilterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private assetRequestSubscription?: Subscription;
  private assetPhotoChanged = false;

  assetForm = this.fb.group({
    name: ['', Validators.required],
    type: ['', Validators.required],
    sku: [''],
    entry_date: [''],
    seal_item: [''],
    category: [''],
    brand: [''],
    subcategory: [''],
    details: [''],
    description: [''],
    photo_url: [''],
    location_name: [''],
    department_id: [null as number | null],
    address: [''],
    kit_id: [null as number | null],
    asset_nature: ['' as InventoryAssetNature],
    hosting_provider: [''],
    environment_name: [''],
    external_identifier: [''],
    cloud_account: [''],
    cloud_region: [''],
    hostname_ip: [''],
    has_reservation: [null as boolean | null],
    reservation_end_date: [''],
    status: ['ACTIVE']
  });

  catalogForm = this.fb.group({
    name: ['', Validators.required],
    parent_id: [null as number | null],
    status: ['ACTIVE']
  });

  kitForm = this.fb.group({
    name: ['', Validators.required],
    code: [''],
    description: [''],
    status: ['ACTIVE']
  });

  assetTypes: string[] = [];
  assetCategories: string[] = [];
  assetSubcategories: string[] = [];
  assetBrands: string[] = [];
  assetLocations: string[] = [];
  readonly catalogTabs: Array<{ value: InventoryCatalogTab; label: string; icon: string }> = [
    { value: 'TYPE', label: 'Tipo', icon: 'inventory_2' },
    { value: 'CATEGORY', label: 'Categoria', icon: 'category' },
    { value: 'SUBCATEGORY', label: 'Subcategoria', icon: 'segment' },
    { value: 'BRAND', label: 'Marca', icon: 'branding_watermark' },
    { value: 'LOCATION', label: 'Local', icon: 'place' }
  ];
  readonly assetStatuses: Array<{ value: InventoryAssetStatus; label: string }> = [
    { value: 'ALL', label: 'Todos' },
    { value: 'ACTIVE', label: 'Em uso' },
    { value: 'STOCK', label: 'Em estoque' },
    { value: 'BROKEN', label: 'Quebrados' }
  ];
  readonly assetFormStatusOptions: Array<{ value: 'ACTIVE' | 'STOCK' | 'BROKEN'; label: string }> = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'STOCK', label: 'Em estoque' },
    { value: 'BROKEN', label: 'Quebrado' }
  ];
  readonly assetNatureOptions: Array<{ value: Exclude<InventoryAssetNature, ''>; label: string }> = [
    { value: 'PHYSICAL', label: 'Físico' },
    { value: 'VIRTUAL', label: 'Virtual' },
    { value: 'CLOUD_SERVICE', label: 'Serviço em nuvem' }
  ];
  readonly hostingProviderOptions = ['Microsoft Azure', 'AWS', 'Google Cloud', 'VMware', 'Proxmox', 'Datacenter local', 'Outro'];
  readonly environmentOptions = ['Produção', 'Homologação', 'Desenvolvimento', 'Teste', 'DR / Contingência', 'Outro'];
  readonly tabs: Array<{ value: InventoryTab; label: string; icon: string }> = [
    { value: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { value: 'assets', label: 'Ativos', icon: 'inventory_2' },
    { value: 'kits', label: 'Kits', icon: 'workspaces' }
  ];

  constructor(
    private fb: FormBuilder,
    private userSession: UserSessionService,
    private orgStructure: OrgStructureService,
    private inventoryService: ControlinventoryService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const current = this.userSession.getUser();
    this.currentUser = current;
    this.tenantId = Number(current?.tenant_id || 0) || null;
    this.userId = Number(current?.id || 0) || null;
    if (this.tenantId) {
      this.loadOrgStructure();
      this.loadAll();
    }
    this.assetForm.get('asset_nature')?.valueChanges.subscribe(() => this.updateInfrastructureFields());
    this.assetForm.get('has_reservation')?.valueChanges.subscribe(() => this.updateReservationValidation());
  }

  ngOnDestroy(): void {
    if (this.assetFilterDebounceTimer) {
      clearTimeout(this.assetFilterDebounceTimer);
    }
    this.assetRequestSubscription?.unsubscribe();
  }

  setActiveMainTab(tab: InventoryTab) {
    this.activeMainTab = tab;
    if (tab === 'dashboard') {
      this.loadDashboard();
    } else if (tab === 'assets') {
      this.loadAssets();
    } else {
      this.loadKits();
    }
  }

  loadAll() {
    this.loadDashboard();
  }

  loadDashboardAssets() {
    if (!this.tenantId) return;
    this.inventoryService.getAssets(this.tenantId, { status: 'ALL', limit: 500, offset: 0 }).subscribe({
      next: (response) => {
        const rows = this.extractAssetRows(response);
        this.dashboardAssets = rows || [];
        this.refreshDashboardFromAssets();
      },
      error: () => {
        this.dashboardAssets = [];
        this.refreshDashboardFromAssets();
      }
    });
  }

  loadDashboard() {
    if (!this.tenantId) return;
    this.loadingDashboard = true;
    this.inventoryService.getDashboard(this.tenantId, this.normalizedDashboardFilters()).subscribe({
      next: (res) => {
      this.dashboard = res;
      this.refreshDashboardDerivedState();
      this.loadingDashboard = false;
      this.historyPageIndex = 0;
      this.refreshHistoryPagination();
    },
      error: () => {
        this.refreshDashboardFromAssets();
        this.loadingDashboard = false;
      }
    });
  }

  applyDashboardFilters() {
    this.refreshDashboardDerivedState();
    if (this.activeMainTab === 'assets') {
      this.assetTypeFilter = String(this.dashboardFilters.type || 'ALL');
      this.assetStatusFilter = String(this.dashboardFilters.status || 'ALL') as InventoryAssetStatus;
      this.assetPageIndex = 0;
      this.loadAssets();
    } else {
      this.loadDashboard();
    }
    this.closeDashboardFilterModal();
  }

  resetDashboardFilters() {
    this.dashboardFilters = {
      type: 'ALL',
      status: 'ALL',
      category: 'ALL',
      location: 'ALL',
      created_by: 'ALL',
      department_id: 'ALL'
    };
    this.refreshDashboardDerivedState();
    if (this.activeMainTab === 'assets') {
      this.assetTypeFilter = 'ALL';
      this.assetStatusFilter = 'ALL';
      this.assetPageIndex = 0;
      this.loadAssets();
    } else {
      this.loadDashboard();
    }
    this.closeDashboardFilterModal();
  }

  openDashboardFilterModal() {
    if (this.activeMainTab === 'assets') {
      this.dashboardFilters.type = this.assetTypeFilter;
      this.dashboardFilters.status = this.assetStatusFilter;
    }
    if (!this.catalogItems.length) {
      this.loadCatalogs();
    }
    this.showDashboardFilterModal = true;
  }

  closeDashboardFilterModal() {
    this.showDashboardFilterModal = false;
  }

  loadAssets() {
    if (!this.tenantId) return;
    if (this.assetFilterDebounceTimer) {
      clearTimeout(this.assetFilterDebounceTimer);
      this.assetFilterDebounceTimer = null;
    }
    this.assetRequestSubscription?.unsubscribe();
    this.loading = true;
    this.assetRequestSubscription = this.inventoryService.getAssets(this.tenantId, {
      search: this.assetSearch,
      sku: this.assetSkuFilter,
      type: this.assetTypeFilter,
      status: this.assetStatusFilter,
      category: this.dashboardFilters.category,
      location: this.dashboardFilters.location,
      created_by: this.dashboardFilters.created_by,
      department_id: this.dashboardFilters.department_id,
      limit: this.assetPageSize,
      offset: this.assetPageIndex * this.assetPageSize
    }).subscribe({
      next: (response) => {
        const rows = this.extractAssetRows(response);
        this.assets = rows || [];
        this.assetsTotal = Number((response as ControlInventoryAssetListResponse)?.total || rows.length || 0);
        this.loading = false;
      },
      error: () => {
        this.assets = [];
        this.assetsTotal = 0;
        this.loading = false;
      }
    });
  }

  loadKits() {
    if (!this.tenantId) return;
    this.loading = true;
    this.inventoryService.getKits(this.tenantId).subscribe({
      next: (rows) => {
        this.kits = rows || [];
        this.refreshDashboardDerivedState();
        if (!this.dashboardTotalAssets && this.dashboardAssets.length) {
          this.refreshDashboardFromAssets();
        }
        this.loading = false;
      },
      error: () => {
        this.kits = [];
        this.refreshDashboardDerivedState();
        if (!this.dashboardTotalAssets && this.dashboardAssets.length) {
          this.refreshDashboardFromAssets();
        }
        this.loading = false;
      }
    });
  }

  loadHistory() {
    if (!this.tenantId) return;
    this.inventoryService.getHistory(this.tenantId, { limit: this.historyMaxRecords }).subscribe({
      next: (rows) => {
        this.history = rows || [];
        this.historyPageIndex = 0;
        this.dashboard = { ...this.dashboard, recent_history: this.history };
        this.refreshHistoryPagination();
      },
      error: () => {
        this.history = [];
        this.historyPageIndex = 0;
        this.refreshHistoryPagination();
      }
    });
  }

  loadCatalogs() {
    if (!this.tenantId) return;
    this.inventoryService.getCatalogs(this.tenantId).subscribe({
      next: (rows) => {
        this.catalogItems = rows || [];
        this.syncCatalogOptions();
        this.refreshCatalogState();
      },
      error: () => {
        this.catalogItems = [];
        this.syncCatalogOptions();
        this.refreshCatalogState();
      }
    });
  }

  openAssetModal(asset?: ControlInventoryAsset) {
    if (!this.catalogItems.length) {
      this.loadCatalogs();
    }
    if (!this.kits.length) {
      this.loadKits();
    }
    this.editingAssetId = asset?.id || null;
    this.assetModalTab = 'details';
    this.assetUserSearch = '';
    this.assetLinkedUserIds = [];
    this.assetHistoryItems = [];
    this.assetHistoryLoading = false;
    this.assetSaveError = null;
    this.assetPhotoChanged = false;
    this.assetForm.reset({
      name: asset?.name || '',
      type: asset?.type || '',
      sku: asset?.sku || '',
      entry_date: this.toDateInputValue(asset?.entry_date) || this.todayInputDate(),
      seal_item: asset?.seal_item || '',
      category: asset?.category || '',
      brand: asset?.brand || '',
      subcategory: asset?.subcategory || '',
      details: asset?.details || '',
      description: asset?.description || '',
      photo_url: '',
      location_name: asset?.location_name || '',
      department_id: asset?.department_id ?? null,
      address: asset?.address || '',
      kit_id: asset?.kit_id ?? null,
      asset_nature: asset?.asset_nature || '',
      hosting_provider: asset?.hosting_provider || '',
      environment_name: asset?.environment_name || '',
      external_identifier: asset?.external_identifier || '',
      cloud_account: asset?.cloud_account || '',
      cloud_region: asset?.cloud_region || '',
      hostname_ip: asset?.hostname_ip || '',
      has_reservation: asset?.asset_nature === 'VIRTUAL' ? Number(asset?.has_reservation) === 1 : null,
      reservation_end_date: this.toDateInputValue(asset?.reservation_end_date) || '',
      status: this.normalizeAssetFormStatus(asset?.status)
    });
    this.updateInfrastructureFields(false);
    this.syncCatalogOptionsForAsset(asset || null);
    this.photoPreviewUrl = asset && asset.has_photo ? this.assetPhotoUrl(asset) : null;
    this.showAssetModal = true;
    this.loadAssetModalAuxData(asset?.id || null);
  }

  closeAssetModal() {
    this.showAssetModal = false;
    this.editingAssetId = null;
    this.assetModalTab = 'details';
    this.assetTenantUsers = [];
    this.assetLinkedUserIds = [];
    this.assetHistoryItems = [];
    this.assetUserSearch = '';
    this.assetUsersLoading = false;
    this.assetHistoryLoading = false;
    this.assetUsersLoadedTenantId = null;
    this.photoPreviewUrl = null;
    this.photoDragActive = false;
    this.assetSaveError = null;
    this.assetPhotoChanged = false;
  }

  openKitModal(kit?: ControlInventoryKit) {
    if (!this.kits.length) {
      this.loadKits();
    }
    this.editingKitId = kit?.id || null;
    this.kitForm.reset({
      name: kit?.name || '',
      code: kit?.code || '',
      description: kit?.description || '',
      status: kit?.status || 'ACTIVE'
    });
    this.showKitModal = true;
  }

  openCatalogModal(tab: InventoryCatalogTab = this.selectedCatalogTab) {
    this.selectedCatalogTab = tab;
    this.editingCatalogId = null;
    this.catalogSaveError = null;
    this.catalogForm.reset({
      name: '',
      parent_id: null,
      status: 'ACTIVE'
    });
    this.showCatalogModal = true;
    this.loadCatalogs();
    this.refreshCatalogState();
  }

  closeCatalogModal() {
    this.showCatalogModal = false;
    this.editingCatalogId = null;
    this.catalogSaveError = null;
    this.catalogForm.reset({
      name: '',
      parent_id: null,
      status: 'ACTIVE'
    });
  }

  selectCatalogTab(tab: InventoryCatalogTab) {
    this.selectedCatalogTab = tab;
    this.editingCatalogId = null;
    this.catalogSaveError = null;
    this.catalogForm.reset({
      name: '',
      parent_id: null,
      status: 'ACTIVE'
    });
    this.refreshCatalogState();
  }

  closeKitModal() {
    this.showKitModal = false;
    this.editingKitId = null;
  }

  saveAsset() {
    this.updateReservationValidation();
    if (!this.tenantId || !this.userId || this.assetForm.invalid) {
      this.assetForm.markAllAsTouched();
      return;
    }
    this.savingAsset = true;
    this.assetSaveError = null;
    this.assetForm.get('sku')?.setErrors(null);
    this.assetForm.get('seal_item')?.setErrors(null);
    const rawEntryDate = String(this.assetForm.get('entry_date')?.value || '').trim();
    const payload: Record<string, any> = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      linked_user_ids: this.assetLinkedUserIds,
      ...this.assetForm.value,
      entry_date: rawEntryDate || null
    };
    if (this.editingAssetId && !this.assetPhotoChanged) {
      delete payload['photo_url'];
    }
    const request$ = this.editingAssetId
      ? this.inventoryService.updateAsset(this.editingAssetId, payload)
      : this.inventoryService.createAsset(payload);
    request$.subscribe({
      next: () => {
        this.savingAsset = false;
        this.closeAssetModal();
        this.refreshActiveTab();
      },
      error: (error) => {
        this.savingAsset = false;
        this.assetSaveError = String(error?.error?.error || 'Não foi possível salvar o ativo.');
        const duplicateFields = Array.isArray(error?.error?.fields) ? error.error.fields : [];
        if (duplicateFields.includes('sku')) {
          this.assetForm.get('sku')?.setErrors({ duplicate: true });
        }
        if (duplicateFields.includes('seal_item')) {
          this.assetForm.get('seal_item')?.setErrors({ duplicate: true });
        }
      }
    });
  }

  isVirtualAsset() {
    return this.assetForm.get('asset_nature')?.value === 'VIRTUAL';
  }

  showsHostedInfrastructureFields() {
    const nature = this.assetForm.get('asset_nature')?.value;
    return nature === 'VIRTUAL' || nature === 'CLOUD_SERVICE';
  }

  assetNatureLabel(value?: string | null) {
    return this.assetNatureOptions.find((option) => option.value === value)?.label || '—';
  }

  private updateInfrastructureFields(clearHiddenValues = true) {
    if (!this.isVirtualAsset() && clearHiddenValues) {
      this.assetForm.patchValue(
        { has_reservation: null, reservation_end_date: '' },
        { emitEvent: false }
      );
    }
    if (!this.showsHostedInfrastructureFields() && clearHiddenValues) {
      this.assetForm.patchValue(
        {
          hosting_provider: '',
          environment_name: '',
          external_identifier: '',
          cloud_account: '',
          cloud_region: '',
          hostname_ip: ''
        },
        { emitEvent: false }
      );
    }
    this.updateReservationValidation();
  }

  private updateReservationValidation() {
    const reservationDate = this.assetForm.get('reservation_end_date');
    const requiresDate = this.isVirtualAsset() && this.assetForm.get('has_reservation')?.value === true;
    reservationDate?.setValidators(requiresDate ? [Validators.required] : []);
    if (!requiresDate && reservationDate?.value) {
      reservationDate.setValue('', { emitEvent: false });
    }
    reservationDate?.updateValueAndValidity({ emitEvent: false });
  }

  clearInventoryFieldError(fieldName: 'sku' | 'seal_item') {
    const control = this.assetForm.get(fieldName);
    if (!control?.errors) {
      return;
    }
    const { duplicate, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
    if (this.assetSaveError && (!this.assetForm.get('sku')?.errors && !this.assetForm.get('seal_item')?.errors)) {
      this.assetSaveError = null;
    }
  }

  saveKit() {
    if (!this.tenantId || !this.userId || this.kitForm.invalid) {
      return;
    }
    this.savingKit = true;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      ...this.kitForm.value
    };
    const request$ = this.editingKitId
      ? this.inventoryService.updateKit(this.editingKitId, payload)
      : this.inventoryService.createKit(payload);
    request$.subscribe({
      next: () => {
        this.savingKit = false;
        this.closeKitModal();
        this.refreshActiveTab();
      },
      error: () => {
        this.savingKit = false;
      }
    });
  }

  saveCatalogItem() {
    if (!this.tenantId || !this.userId || this.catalogForm.invalid) {
      return;
    }
    const name = String(this.catalogForm.get('name')?.value || '').trim();
    if (!name) {
      return;
    }
    const parentId = Number(this.catalogForm.get('parent_id')?.value || 0) || null;
    if (this.selectedCatalogTab === 'SUBCATEGORY' && !parentId) {
      this.catalogSaveError = 'Selecione uma categoria para a subcategoria.';
      return;
    }
    this.savingCatalog = true;
    this.catalogSaveError = null;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      catalog_type: this.selectedCatalogTab,
      name,
      parent_id: this.selectedCatalogTab === 'SUBCATEGORY' ? parentId : null,
      status: this.catalogForm.get('status')?.value || 'ACTIVE'
    };
    const request$ = this.editingCatalogId
      ? this.inventoryService.updateCatalog(this.editingCatalogId, payload)
      : this.inventoryService.createCatalog(payload);
    request$.subscribe({
      next: () => {
        this.savingCatalog = false;
        this.loadCatalogs();
        this.editingCatalogId = null;
        this.catalogSaveError = null;
        this.catalogForm.reset({
          name: '',
          parent_id: null,
          status: 'ACTIVE'
        });
      },
      error: (error) => {
        this.savingCatalog = false;
        this.catalogSaveError = String(error?.error?.error || 'Não foi possível salvar o cadastro.');
      }
    });
  }

  editCatalogItem(item: ControlInventoryCatalogItem) {
    this.selectedCatalogTab = item.catalog_type;
    this.editingCatalogId = item.id;
    this.catalogSaveError = null;
    this.catalogForm.reset({
      name: item.name || '',
      parent_id: item.parent_id ?? null,
      status: item.status || 'ACTIVE'
    });
    this.showCatalogModal = true;
  }

  deactivateCatalog(item: ControlInventoryCatalogItem, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) return;
    if (!confirm(`Inativar o cadastro "${item.name}"?`)) return;
    this.inventoryService.deactivateCatalog(this.tenantId, item.id, this.userId).subscribe({
      next: () => this.loadCatalogs()
    });
  }

  deactivateAsset(asset: ControlInventoryAsset, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) return;
    if (!confirm(`Excluir o ativo "${asset.name}"?`)) return;
    this.inventoryService.deactivateAsset(this.tenantId, asset.id, this.userId).subscribe({
      next: () => this.refreshActiveTab()
    });
  }

  deactivateKit(kit: ControlInventoryKit, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) return;
    if (!confirm(`Inativar o kit "${kit.name}"?`)) return;
    this.inventoryService.deactivateKit(this.tenantId, kit.id, this.userId).subscribe({
      next: () => this.refreshActiveTab()
    });
  }

  openPhotoPicker() {
    this.photoFileInput?.nativeElement?.click();
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) {
      return;
    }
    this.readPhotoFile(file);
    input.value = '';
  }

  onPhotoDragOver(event: DragEvent) {
    event.preventDefault();
    this.photoDragActive = true;
  }

  onPhotoDragLeave(event: DragEvent) {
    event.preventDefault();
    this.photoDragActive = false;
  }

  onPhotoDrop(event: DragEvent) {
    event.preventDefault();
    this.photoDragActive = false;
    const file = event.dataTransfer?.files?.[0] || null;
    if (!file) {
      return;
    }
    this.readPhotoFile(file);
  }

  clearPhoto(event?: Event) {
    event?.stopPropagation();
    this.assetPhotoChanged = true;
    this.photoPreviewUrl = null;
    this.assetForm.patchValue({ photo_url: '' });
  }

  onAssetFilterChange(debounce = true) {
    this.assetPageIndex = 0;
    this.assetRequestSubscription?.unsubscribe();
    this.assets = [];
    this.assetsTotal = 0;
    this.loading = true;
    if (this.assetFilterDebounceTimer) {
      clearTimeout(this.assetFilterDebounceTimer);
    }
    if (!debounce) {
      this.assetFilterDebounceTimer = null;
      this.loadAssets();
      return;
    }
    this.assetFilterDebounceTimer = setTimeout(() => {
      this.assetFilterDebounceTimer = null;
      this.loadAssets();
    }, 300);
  }

  assetPhotoUrl(asset: ControlInventoryAsset): string {
    return this.tenantId ? this.inventoryService.getAssetPhotoUrl(this.tenantId, asset) : '';
  }

  onAssetCategoryChange() {
    const category = String(this.assetForm.get('category')?.value || '').trim();
    this.assetSubcategories = this.getCatalogNames('SUBCATEGORY', category);
    const currentSubcategory = String(this.assetForm.get('subcategory')?.value || '').trim();
    if (currentSubcategory && !this.assetSubcategories.some((value) => value.toLowerCase() === currentSubcategory.toLowerCase())) {
      this.assetForm.patchValue({ subcategory: '' });
    }
  }

  onAssetDepartmentChange() {
    return;
  }

  setAssetModalTab(tab: InventoryAssetModalTab) {
    this.assetModalTab = tab;
    if (tab === 'history' && this.editingAssetId && !this.assetHistoryItems.length && !this.assetHistoryLoading) {
      this.loadAssetHistory(this.editingAssetId);
    }
  }

  isAssetUserLinked(userId: number) {
    return this.assetLinkedUserIds.includes(Number(userId));
  }

  toggleAssetUserSelection(userId: number, checked: boolean) {
    const normalizedUserId = Number(userId);
    if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) {
      return;
    }
    const current = new Set(this.assetLinkedUserIds.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0));
    if (checked) {
      current.add(normalizedUserId);
    } else {
      current.delete(normalizedUserId);
    }
    this.assetLinkedUserIds = Array.from(current.values()).sort((a, b) => a - b);
  }

  trackByAssetUserId(_index: number, item: InventoryTenantUser) {
    return item?.id || _index;
  }

  trackByAssetHistoryId(_index: number, item: ControlInventoryHistoryItem) {
    return item?.id || _index;
  }

  formatHistoryTimestamp(value?: string | null) {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('pt-BR');
  }

  assetHistoryActionLabel(actionType?: string | null) {
    const normalized = String(actionType || '').trim().toUpperCase();
    const map: Record<string, string> = {
      CREATE: 'Criado',
      UPDATE: 'Atualizado',
      DELETE: 'Excluído',
      LINK_USER: 'Vinculado',
      UNLINK_USER: 'Desvinculado'
    };
    return map[normalized] || actionType || '—';
  }

  get filteredAssetTenantUsers() {
    const search = String(this.assetUserSearch || '').trim().toLowerCase();
    const selected = new Set(this.assetLinkedUserIds.map((value) => Number(value)));
    return this.assetTenantUsers
      .slice()
      .filter((user) => {
        if (!search) return true;
        const haystack = `${user.name || ''} ${user.email || ''}`.toLowerCase();
        return haystack.includes(search);
      })
      .sort((a, b) => {
        const aSelected = selected.has(Number(a.id)) ? 0 : 1;
        const bSelected = selected.has(Number(b.id)) ? 0 : 1;
        if (aSelected !== bSelected) return aSelected - bSelected;
        return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' });
      });
  }

  get selectedAssetTenantUsers() {
    const selected = new Set(this.assetLinkedUserIds.map((value) => Number(value)));
    return this.filteredAssetTenantUsers.filter((user) => selected.has(Number(user.id)));
  }

  get availableAssetTenantUsers() {
    const selected = new Set(this.assetLinkedUserIds.map((value) => Number(value)));
    return this.filteredAssetTenantUsers.filter((user) => !selected.has(Number(user.id)));
  }

  private loadAssetModalAuxData(assetId: number | null) {
    this.loadTenantUsersForAssetModal();
    if (assetId) {
      this.loadAssetLinkedUsers(assetId);
      this.loadAssetHistory(assetId);
    }
  }

  private loadTenantUsersForAssetModal() {
    if (!this.tenantId) return;
    if (this.assetUsersLoadedTenantId === this.tenantId && this.assetTenantUsers.length) {
      return;
    }
    this.assetUsersLoading = true;
    this.userService.getUsersByTenant(this.tenantId).subscribe({
      next: (rows) => {
        this.assetTenantUsers = (rows || [])
          .map((row: any) => ({
            id: Number(row?.id || 0),
            name: String(row?.name || row?.email || 'Usuário').trim(),
            email: row?.email ? String(row.email).trim() : null,
            active: row?.active ?? null
          }))
          .filter((row) => Number.isInteger(row.id) && row.id > 0);
        this.assetUsersLoadedTenantId = this.tenantId;
        this.assetUsersLoading = false;
      },
      error: () => {
        this.assetTenantUsers = [];
        this.assetUsersLoadedTenantId = null;
        this.assetUsersLoading = false;
      }
    });
  }

  private loadAssetLinkedUsers(assetId: number) {
    if (!this.tenantId) return;
    this.inventoryService.getAssetUsers(this.tenantId, assetId).subscribe({
      next: (rows: ControlInventoryAssetUser[]) => {
        this.assetLinkedUserIds = Array.from(
          new Set(
            (rows || [])
              .map((row) => Number(row?.user_id || 0))
              .filter((value) => Number.isInteger(value) && value > 0)
          )
        ).sort((a, b) => a - b);
      },
      error: () => {
        this.assetLinkedUserIds = [];
      }
    });
  }

  private loadAssetHistory(assetId: number) {
    if (!this.tenantId) return;
    this.assetHistoryLoading = true;
    this.inventoryService.getHistory(this.tenantId, {
      entity_type: 'ASSET',
      entity_id: assetId,
      limit: 100
    }).subscribe({
      next: (rows) => {
        this.assetHistoryItems = rows || [];
        this.assetHistoryLoading = false;
      },
      error: () => {
        this.assetHistoryItems = [];
        this.assetHistoryLoading = false;
      }
    });
  }

  onDashboardFilterChange() {
    return;
  }

  previousHistoryPage() {
    if (this.historyPageIndex > 0) {
      this.historyPageIndex -= 1;
      this.refreshHistoryPagination();
    }
  }

  nextHistoryPage() {
    if (this.historyPageIndex < this.historyPageCount - 1) {
      this.historyPageIndex += 1;
      this.refreshHistoryPagination();
    }
  }

  previousAssetPage() {
    if (this.assetPageIndex > 0) {
      this.assetPageIndex -= 1;
      this.loadAssets();
    }
  }

  nextAssetPage() {
    if (this.assetPageIndex < this.assetPageCount - 1) {
      this.assetPageIndex += 1;
      this.loadAssets();
    }
  }

  statusLabel(value?: string | null) {
    const map: Record<string, string> = {
      ACTIVE: 'Ativo',
      INACTIVE: 'Inativo',
      BROKEN: 'Quebrado',
      ALL: 'Todos'
    };
    return map[String(value || '')] || value || '—';
  }

  assetStatusLabel(value?: string | null) {
    const map: Record<string, string> = {
      ACTIVE: 'Ativo',
      STOCK: 'Em estoque',
      INACTIVE: 'Em estoque',
      BROKEN: 'Quebrado',
      ALL: 'Todos'
    };
    return map[String(value || '')] || value || '—';
  }

  isAssetInStock(value?: string | null) {
    const normalized = String(value || '').trim().toUpperCase();
    return normalized === 'STOCK' || normalized === 'INACTIVE';
  }

  assetTypeLabel(value?: string | null) {
    const map: Record<string, string> = {
      monitor: 'Monitor',
      maquina: 'Máquina',
      cpu: 'CPU',
      mesa: 'Mesa',
      cadeira: 'Cadeira'
    };
    const normalized = String(value || '').trim();
    if (!normalized) return '—';
    return map[normalized] || this.titleize(normalized);
  }

  catalogTabLabel(tab: InventoryCatalogTab) {
    const found = this.catalogTabs.find((item) => item.value === tab);
    return found?.label || tab;
  }

  canManageCatalogs() {
    const user = this.currentUser || this.userSession.getUser();
    const roleName = String(user?.role_name || user?.role?.name || '').toLowerCase();
    const roleId = Number(user?.role_id || user?.role?.id || 0);
    const isMaster = !!user?.is_master || roleName.includes('master') || roleName === 'admin' || roleId === 1;
    if (isMaster) {
      return true;
    }
    const modules = Array.isArray(user?.modules) ? user.modules : [];
    const module = modules.find((entry: any) => String(entry?.module_key || '').toLowerCase() === 'controlinventory');
    return !!(module?.can_manage || module?.can_create);
  }

  formatDate(value?: string | null) {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('pt-BR');
  }

  private toDateInputValue(value?: string | null) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  }

  private normalizeAssetFormStatus(value?: string | null): 'ACTIVE' | 'STOCK' | 'BROKEN' {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'INACTIVE') {
      return 'STOCK';
    }
    if (normalized === 'STOCK' || normalized === 'BROKEN' || normalized === 'ACTIVE') {
      return normalized;
    }
    return 'ACTIVE';
  }

  setDashboardPeriod(months: number) {
    this.dashboardPeriodMonths = months;
    this.monthlyRegistrationsChartCacheSignature = '';
  }

  private refreshActiveTab() {
    if (this.activeMainTab === 'assets') {
      this.loadAssets();
      return;
    }
    if (this.activeMainTab === 'kits') {
      this.loadKits();
      return;
    }
    this.loadDashboard();
  }

  private todayInputDate(): string {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
  }

  private async readPhotoFile(file: File) {
    if (!file.type.startsWith('image/')) {
      return;
    }
    try {
      const result = await this.compressImageFile(file);
      this.assetPhotoChanged = true;
      this.photoPreviewUrl = result;
      this.assetForm.patchValue({ photo_url: result });
    } catch (_error) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        this.assetPhotoChanged = true;
        this.photoPreviewUrl = result;
        this.assetForm.patchValue({ photo_url: result });
      };
      reader.readAsDataURL(file);
    }
  }

  private compressImageFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Falha ao ler a imagem.'));
      reader.onload = () => {
        const source = String(reader.result || '');
        const image = new Image();
        image.onerror = () => reject(new Error('Falha ao carregar a imagem.'));
        image.onload = () => {
          const maxWidth = 1400;
          const maxHeight = 1400;
          const ratio = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.width * ratio));
          canvas.height = Math.max(1, Math.round(image.height * ratio));
          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('Canvas indisponível.'));
            return;
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        image.src = source;
      };
      reader.readAsDataURL(file);
    });
  }

  private syncCatalogOptions() {
    const appendCurrentValue = (items: string[], value?: string | null) => {
      const current = String(value || '').trim();
      if (!current) return items;
      const exists = items.some((item) => String(item || '').trim().toLowerCase() === current.toLowerCase());
      return exists ? items : [current, ...items];
    };

    this.assetTypes = this.getCatalogNames('TYPE');
    this.assetCategories = this.getCatalogNames('CATEGORY');
    this.assetSubcategories = appendCurrentValue(
      this.getCatalogNames('SUBCATEGORY', this.assetForm.get('category')?.value),
      this.assetForm.get('subcategory')?.value
    );
    this.assetBrands = this.getCatalogNames('BRAND');
    this.assetLocations = this.getCatalogNames('LOCATION');
    this.refreshDashboardDerivedState();
  }

  private getCatalogNames(type: InventoryCatalogTab, parentCategory?: string | null) {
    const normalizedParent = String(parentCategory || '').trim().toLowerCase();
    return this.catalogItems
      .filter((item) => String(item.catalog_type || '').toUpperCase() === type)
      .filter((item) => {
        if (type !== 'SUBCATEGORY') {
          return true;
        }
        if (!normalizedParent) {
          return false;
        }
        return String(item.parent_name || '').trim().toLowerCase() === normalizedParent;
      })
      .map((item) => String(item.name || '').trim())
      .filter((value) => !!value);
  }

  private syncCatalogOptionsForAsset(asset?: ControlInventoryAsset | null) {
    const appendCurrentValue = (items: string[], value?: string | null) => {
      const current = String(value || '').trim();
      if (!current) return items;
      const exists = items.some((item) => String(item || '').trim().toLowerCase() === current.toLowerCase());
      return exists ? items : [current, ...items];
    };

    this.assetTypes = appendCurrentValue(this.getCatalogNames('TYPE'), asset?.type);
    this.assetCategories = appendCurrentValue(this.getCatalogNames('CATEGORY'), asset?.category);
    this.assetSubcategories = appendCurrentValue(this.getCatalogNames('SUBCATEGORY', asset?.category), asset?.subcategory);
    this.assetBrands = appendCurrentValue(this.getCatalogNames('BRAND'), asset?.brand);
    this.assetLocations = appendCurrentValue(this.getCatalogNames('LOCATION'), asset?.location_name);
  }

  private loadOrgStructure() {
    if (!this.tenantId) return;
    this.orgStructure.getDepartments(this.tenantId).subscribe({
      next: (rows) => {
        this.departments = Array.isArray(rows) ? rows : [];
        this.refreshDepartmentOptions();
      },
      error: () => {
        this.departments = [];
        this.refreshDepartmentOptions();
      }
    });
  }

  catalogItemSubtitle(item: ControlInventoryCatalogItem) {
    const details: string[] = [this.statusLabel(item.status)];
    if (String(item.catalog_type || '').toUpperCase() === 'SUBCATEGORY' && item.parent_name) {
      details.push(`Categoria: ${item.parent_name}`);
    }
    details.push(`Criado por: ${item.created_by_name || item.created_by || '—'}`);
    return details.join(' · ');
  }

  private titleize(value: string) {
    return String(value || '')
      .toLowerCase()
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  assetDepartmentLabel(departmentId: any, departmentName?: string | null, sectorName?: string | null) {
    const fallbackDepartment = this.departments.find((item) => String(item.id) === String(departmentId));
    const resolvedDepartmentName = String(departmentName || fallbackDepartment?.name || '').trim();
    const resolvedSectorName = String(sectorName || fallbackDepartment?.sector_name || '').trim();
    if (!resolvedDepartmentName) return '—';
    const sectorValue = resolvedSectorName;
    const departmentValue = resolvedDepartmentName;
    return sectorValue ? `${sectorValue} / ${departmentValue}` : departmentValue;
  }

  private refreshCatalogState() {
    this.currentCatalogItems = this.catalogItems
      .filter((item) => String(item.catalog_type || '').toUpperCase() === this.selectedCatalogTab)
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));

    const activeCategories = this.catalogItems
      .filter((item) => String(item.catalog_type || '').toUpperCase() === 'CATEGORY')
      .filter((item) => String(item.status || '').toUpperCase() === 'ACTIVE')
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));
    const currentParentId = Number(this.catalogForm.get('parent_id')?.value || 0) || null;
    if (currentParentId && !activeCategories.some((item) => Number(item.id) === currentParentId)) {
      const currentParent = this.catalogItems.find((item) => Number(item.id) === currentParentId);
      this.catalogCategoryOptions = currentParent ? [currentParent, ...activeCategories] : activeCategories;
    } else {
      this.catalogCategoryOptions = activeCategories;
    }
  }

  private refreshDepartmentOptions() {
    this.assetDepartmentOptions = this.departments
      .slice()
      .sort((a, b) => {
        const sectorCompare = String(a.sector_name || '').localeCompare(String(b.sector_name || ''), 'pt-BR', { sensitivity: 'base' });
        if (sectorCompare !== 0) return sectorCompare;
        return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' });
      });
    this.refreshDashboardDerivedState();
  }

  private refreshHistoryPagination() {
    const start = this.historyPageIndex * this.historyPageSize;
    this.pagedHistory = this.history.slice(start, start + this.historyPageSize);
    this.historyPageCount = Math.max(1, Math.ceil((this.history.length || 0) / this.historyPageSize));
    this.historyPageStart = this.history.length ? this.historyPageIndex * this.historyPageSize + 1 : 0;
    this.historyPageEnd = Math.min((this.historyPageIndex + 1) * this.historyPageSize, this.history.length);
  }

  get pagedAssets() {
    return this.assets;
  }

  get assetPageCount() {
    return Math.max(1, Math.ceil((this.assetsTotal || 0) / this.assetPageSize));
  }

  get assetPageStart() {
    return this.assetsTotal ? this.assetPageIndex * this.assetPageSize + 1 : 0;
  }

  get assetPageEnd() {
    return Math.min((this.assetPageIndex + 1) * this.assetPageSize, this.assetsTotal);
  }

  private refreshDashboardDerivedState() {
    this.activeAssetsCount = Number(this.dashboard?.active_assets || 0);
    this.inactiveAssetsCount = Number(this.dashboard?.inactive_assets || 0);
    this.kitsCount = Number(this.dashboard?.kits || 0);
    this.damagedAssetsCount = Number(this.dashboard?.damaged_assets || 0);
    this.assetsWithKitCount = Number(this.dashboard?.assets_with_kit || 0);
    this.assetsWithoutKitCount = Number(this.dashboard?.assets_without_kit || 0);
    this.dashboardTotalAssets = this.activeAssetsCount + this.inactiveAssetsCount;
    this.dashboardActiveRate = this.dashboardTotalAssets
      ? Number(((this.activeAssetsCount / this.dashboardTotalAssets) * 100).toFixed(1))
      : 0;
    this.dashboardKitCoverageRate = this.dashboardTotalAssets
      ? Number(((this.assetsWithKitCount / this.dashboardTotalAssets) * 100).toFixed(1))
      : 0;
    this.dashboardDamageRate = this.dashboardTotalAssets
      ? Number(((this.damagedAssetsCount / this.dashboardTotalAssets) * 100).toFixed(1))
      : 0;

    this.assetTypeBars = this.buildBars(
      (this.dashboard?.by_type || []).map((row) => ({
        label: this.assetTypeLabel(row.type),
        total: Number(row.total || 0)
      })),
      ['#22b8b3', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff']
    );
    this.assetCategoryBars = this.buildBars(
      (this.dashboard?.by_category || []).map((row) => ({
        label: String(row.label || 'Sem categoria'),
        total: Number(row.total || 0)
      })),
      ['#4c6ef5', '#22b8b3', '#f0bf45', '#ef6464', '#7c8cff', '#7dd3fc']
    );
    this.assetDepartmentBars = this.buildBars(this.dashboard?.by_department || [], ['#14b8a6', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff', '#8b5cf6']);
    this.assetCreatedByBars = this.buildBars(this.dashboard?.by_created_by || [], ['#2d6a4f', '#4c6ef5', '#22b8b3', '#f0bf45', '#ef6464', '#7c8cff']);
    this.setCategoryDonut(
      (this.dashboard?.by_category || []).map((row) => ({
        label: String(row.label || 'Sem categoria'),
        total: Number(row.total || 0)
      }))
    );
    this.setDepartmentDonut(
      (this.dashboard?.by_department || []).map((row) => ({
        label: row.label,
        total: Number(row.total || 0)
      }))
    );

    const top = (rows: Array<{ label: string; count: number }>) => {
      const first = rows[0];
      return first ? `${first.label} (${first.count})` : 'Sem dados';
    };
    this.dashboardTopType = top(this.assetTypeBars);
    this.dashboardTopCategory = top(this.assetCategoryBars);
    this.dashboardTopDepartment = top(this.assetDepartmentBars);

    this.dashboardCreatorOptions = (this.dashboard?.by_created_by || [])
      .filter((row) => Number(row.user_id || 0) > 0)
      .map((row) => ({ value: String(row.user_id), label: row.label }));
    this.dashboardDepartmentFilterOptions = [
      { value: 'ALL', label: 'Todos os departamentos' },
      ...this.assetDepartmentOptions
        .filter((department) => Number(department.id || 0) > 0)
        .map((department) => ({
          value: String(department.id),
          label: department.sector_name ? `${department.sector_name} / ${department.name}` : department.name
        }))
    ];
    this.dashboardTypeOptions = [
      { value: 'ALL', label: 'Todos os tipos' },
      ...this.assetTypes.map((type) => ({ value: type, label: this.assetTypeLabel(type) }))
    ];
    this.dashboardStatusOptions = [
      { value: 'ALL', label: 'Todos os status' },
      { value: 'ACTIVE', label: 'Em uso' },
      { value: 'STOCK', label: 'Em estoque' },
      { value: 'BROKEN', label: 'Quebrado' }
    ];
    this.dashboardCategoryOptions = [
      { value: 'ALL', label: 'Todas as categorias' },
      ...this.assetCategories.map((category) => ({ value: category, label: category }))
    ];
    this.dashboardLocationOptions = [
      { value: 'ALL', label: 'Todos os locais' },
      ...this.assetLocations.map((location) => ({ value: location, label: location }))
    ];
    this.dashboardActiveFiltersCount = Object.entries(this.dashboardFilters).filter(([, value]) => value && value !== 'ALL').length;
    this.dashboardFilterSummary = this.buildDashboardFilterSummary();
  }

  private refreshDashboardFromAssets() {
    const rows = this.filterDashboardAssets(Array.isArray(this.dashboardAssets) ? this.dashboardAssets : []);
    const activeRows = rows.filter((row) => String(row.status || '').toUpperCase() === 'ACTIVE');
    const inactiveRows = rows.filter((row) => this.isAssetInStock(row.status));
    const damagedRows = rows.filter((row) => String(row.status || '').toUpperCase() === 'BROKEN');
    const withKitRows = rows.filter((row) => Number(row.kit_id || 0) > 0);
    const withoutKitRows = rows.filter((row) => !Number(row.kit_id || 0));

    const buildGroup = (items: ControlInventoryAsset[], keyFn: (row: ControlInventoryAsset) => string, labelFn?: (value: string) => string) => {
      const bucket = new Map<string, number>();
      items.forEach((row) => {
        const raw = String(keyFn(row) || '').trim();
        const key = raw || 'Sem dados';
        bucket.set(key, (bucket.get(key) || 0) + 1);
      });
      return Array.from(bucket.entries())
        .map(([label, total]) => ({ label: labelFn ? labelFn(label) : label, total }))
        .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }));
    };

    const creatorGroups = buildGroup(rows, (row) => `${row.created_by || 0}::${row.created_by_name || 'Sem executor'}`, (value) => value.split('::')[1] || 'Sem executor');
    const typeGroups = buildGroup(rows, (row) => row.type || 'Sem tipo', (value) => this.assetTypeLabel(value));
    const categoryGroups = buildGroup(rows, (row) => row.category || 'Sem categoria');
    const brandGroups = buildGroup(rows, (row) => row.brand || 'Sem marca');
    const locationGroups = buildGroup(rows, (row) => row.location_name || 'Sem local');
    const departmentGroups = buildGroup(rows, (row) => this.assetDepartmentLabel(row.department_id, row.department_name, row.sector_name));
    this.setCategoryDonut(
      categoryGroups.map((row) => ({
        label: row.label,
        total: Number(row.total || 0)
      }))
    );
    this.setDepartmentDonut(
      departmentGroups.map((row) => ({
        label: row.label,
        total: Number(row.total || 0)
      }))
    );

    this.activeAssetsCount = activeRows.length;
    this.inactiveAssetsCount = inactiveRows.length;
    this.kitsCount = this.kits.length;
    this.damagedAssetsCount = damagedRows.length;
    this.assetsWithKitCount = withKitRows.length;
    this.assetsWithoutKitCount = withoutKitRows.length;
    this.dashboardTotalAssets = rows.length;
    this.dashboardActiveRate = rows.length ? Number(((activeRows.length / rows.length) * 100).toFixed(1)) : 0;
    this.dashboardDamageRate = rows.length ? Number(((damagedRows.length / rows.length) * 100).toFixed(1)) : 0;
    this.assetTypeBars = this.buildBars(typeGroups, ['#22b8b3', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff']);
    this.assetCategoryBars = this.buildBars(categoryGroups, ['#4c6ef5', '#22b8b3', '#f0bf45', '#ef6464', '#7c8cff', '#7dd3fc']);
    this.assetBrandBars = this.buildBars(brandGroups, ['#8b5cf6', '#22b8b3', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff']);
    this.assetLocationBars = this.buildBars(locationGroups, ['#0ea5e9', '#22b8b3', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff']);
    this.assetDepartmentBars = this.buildBars(departmentGroups, ['#14b8a6', '#4c6ef5', '#f0bf45', '#ef6464', '#7c8cff', '#8b5cf6']);
    this.assetCreatedByBars = this.buildBars(creatorGroups, ['#2d6a4f', '#4c6ef5', '#22b8b3', '#f0bf45', '#ef6464', '#7c8cff']);
    this.dashboardTopType = this.assetTypeBars[0] ? `${this.assetTypeBars[0].label} (${this.assetTypeBars[0].count})` : 'Sem dados';
    this.dashboardTopCategory = this.assetCategoryBars[0] ? `${this.assetCategoryBars[0].label} (${this.assetCategoryBars[0].count})` : 'Sem dados';
    this.dashboardTopCreator = this.assetCreatedByBars[0] ? `${this.assetCreatedByBars[0].label} (${this.assetCreatedByBars[0].count})` : 'Sem dados';
    this.dashboardTopDepartment = this.assetDepartmentBars[0] ? `${this.assetDepartmentBars[0].label} (${this.assetDepartmentBars[0].count})` : 'Sem dados';
    this.dashboardCreatorOptions = creatorGroups
      .map((row) => {
        const [id, label] = String(row.label || '').split('::');
        return { value: id, label: label || 'Sem executor' };
      })
      .filter((row) => Number(row.value || 0) > 0);
    this.dashboardDepartmentFilterOptions = [
      { value: 'ALL', label: 'Todos os departamentos' },
      ...departmentGroups
        .filter((row) => row.label !== 'Sem departamento')
        .map((row) => ({ value: row.label, label: row.label }))
    ];
    this.dashboardTypeOptions = [
      { value: 'ALL', label: 'Todos os tipos' },
      ...typeGroups.map((row) => ({ value: row.label, label: row.label }))
    ];
    this.dashboardStatusOptions = [
      { value: 'ALL', label: 'Todos os status' },
      { value: 'ACTIVE', label: 'Em uso' },
      { value: 'STOCK', label: 'Em estoque' },
      { value: 'BROKEN', label: 'Quebrado' }
    ];
    this.dashboardCategoryOptions = [
      { value: 'ALL', label: 'Todas as categorias' },
      ...categoryGroups.map((row) => ({ value: row.label, label: row.label }))
    ];
    this.dashboardLocationOptions = [
      { value: 'ALL', label: 'Todos os locais' },
      ...locationGroups.map((row) => ({ value: row.label, label: row.label }))
    ];
    this.dashboardActiveFiltersCount = Object.entries(this.dashboardFilters).filter(([, value]) => value && value !== 'ALL').length;
    this.dashboardFilterSummary = this.buildDashboardFilterSummary();
  }

  private buildDashboardFilterSummary() {
    const parts: string[] = [];
    const type = String(this.dashboardFilters.type || '').toUpperCase();
    if (type && type !== 'ALL') parts.push(`Tipo: ${this.assetTypeLabel(type)}`);
    const status = String(this.dashboardFilters.status || '').toUpperCase();
    const statusLabel = this.dashboardStatusOptions.find((item) => item.value === status)?.label;
    if (status && status !== 'ALL' && statusLabel) parts.push(`Status: ${statusLabel}`);
    const category = String(this.dashboardFilters.category || '').toUpperCase();
    if (category && category !== 'ALL') parts.push(`Categoria: ${category}`);
    const location = String(this.dashboardFilters.location || '').toUpperCase();
    if (location && location !== 'ALL') parts.push(`Local: ${location}`);
    const creator = this.dashboardCreatorOptions.find((item) => item.value === String(this.dashboardFilters.created_by));
    if (creator) parts.push(`Criado por: ${creator.label}`);
    const department = this.dashboardDepartmentFilterOptions.find((item) => item.value === String(this.dashboardFilters.department_id));
    if (department && department.value !== 'ALL') parts.push(`Departamento: ${department.label}`);
    return parts.length ? parts : ['Sem filtros aplicados'];
  }

  private formatMonthLabel(date: Date) {
    return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '').replace(/^./, (char) => char.toUpperCase());
  }

  private buildConicGradient(segments: Array<{ color: string; percent: number }>) {
    if (!segments.length) {
      return 'conic-gradient(#e5ebf6 0deg 360deg)';
    }
    let current = 0;
    const pieces = segments.map((segment) => {
      const start = current;
      current += Math.max(0, segment.percent) * 3.6;
      return `${segment.color} ${start.toFixed(1)}deg ${current.toFixed(1)}deg`;
    });
    if (current < 360) {
      pieces.push(`#e5ebf6 ${current.toFixed(1)}deg 360deg`);
    }
    return `conic-gradient(${pieces.join(', ')})`;
  }

  private setDepartmentDonut(rows: Array<{ label: string; total: number }>) {
    const departmentTotal = rows.reduce((acc, row) => acc + Number(row.total || 0), 0);
    const topDepartments = rows.slice(0, 4);
    const remainingDepartmentTotal = rows.slice(4).reduce((acc, row) => acc + Number(row.total || 0), 0);
    const donutBase = topDepartments.map((row, index) => ({
      label: row.label,
      count: Number(row.total || 0),
      percent: departmentTotal ? Number(((Number(row.total || 0) / departmentTotal) * 100).toFixed(1)) : 0,
      color: ['#22b8b3', '#4c6ef5', '#f0bf45', '#7c8cff'][index % 4]
    }));
    if (remainingDepartmentTotal > 0) {
      donutBase.push({
        label: 'Outros',
        count: remainingDepartmentTotal,
        percent: departmentTotal ? Number(((remainingDepartmentTotal / departmentTotal) * 100).toFixed(1)) : 0,
        color: '#d9e2f1'
      });
    }
    this.departmentDonutSegments = donutBase;
    this.departmentDonutTotal = departmentTotal;
    this.departmentDonutGradient = this.buildConicGradient(donutBase);
  }

  private setCategoryDonut(rows: Array<{ label: string; total: number }>) {
    const categoryTotal = rows.reduce((acc, row) => acc + Number(row.total || 0), 0);
    const topCategories = rows.slice(0, 4);
    const remainingCategoryTotal = rows.slice(4).reduce((acc, row) => acc + Number(row.total || 0), 0);
    const donutBase = topCategories.map((row, index) => ({
      label: row.label,
      count: Number(row.total || 0),
      percent: categoryTotal ? Number(((Number(row.total || 0) / categoryTotal) * 100).toFixed(1)) : 0,
      color: ['#4c6ef5', '#22b8b3', '#f0bf45', '#7c8cff'][index % 4]
    }));
    if (remainingCategoryTotal > 0) {
      donutBase.push({
        label: 'Outras',
        count: remainingCategoryTotal,
        percent: categoryTotal ? Number(((remainingCategoryTotal / categoryTotal) * 100).toFixed(1)) : 0,
        color: '#d9e2f1'
      });
    }
    this.categoryDonutSegments = donutBase;
    this.categoryDonutTotal = categoryTotal;
    this.categoryDonutGradient = this.buildConicGradient(donutBase);
  }

  private extractAssetRows(response: ControlInventoryAssetListResponse | ControlInventoryAsset[] | null | undefined) {
    if (Array.isArray(response)) {
      return response;
    }
    return Array.isArray(response?.rows) ? response.rows : [];
  }

  private getMonthlySeries(): Array<{ month_key: string; total: number }> {
    const rows = Array.isArray(this.dashboard?.by_created_month) ? this.dashboard.by_created_month : [];
    return rows
      .map((row) => ({
        month_key: String(row.month_key || '').trim(),
        total: Number(row.total || 0)
      }))
      .filter((row) => !!row.month_key);
  }

  get dashboardMonthSummaryLabel() {
    const current = this.getMonthlySeries().slice(-1)[0];
    const total = Number(current?.total || 0);
    const monthLabel = current?.month_key ? this.formatMonthLabelFromKey(current.month_key) : 'mês atual';
    return `${total} cadastros em ${monthLabel}`;
  }

  get inventoryRegistrationsLineChartData(): ChartData<'line'> {
    const periodMonths = this.dashboardPeriodMonths || 12;
    const rows = this.getMonthlySeries();
    const monthMap = new Map<string, number>();
    const monthMeta: Array<{ key: string; label: string }> = [];
    for (let offset = periodMonths - 1; offset >= 0; offset -= 1) {
      const date = new Date();
      date.setMonth(date.getMonth() - offset, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthMeta.push({ key, label: this.formatMonthLabel(date) });
      monthMap.set(key, 0);
    }
    rows.forEach((row) => {
      if (monthMap.has(row.month_key)) {
        monthMap.set(row.month_key, Number(row.total || 0));
      }
    });
    const values = monthMeta.map((item) => Number(monthMap.get(item.key) || 0));
    const average = values.length ? Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1)) : 0;
    const trend = values.map((value, index) => {
      const prev = values[index - 1] ?? value;
      const next = values[index + 1] ?? value;
      return Number(((prev + value + next) / 3).toFixed(1));
    });
    const signature = JSON.stringify({ monthMeta, values, trend, average });
    if (signature === this.monthlyRegistrationsChartCacheSignature) {
      return this.monthlyRegistrationsChartCache;
    }
    this.monthlyRegistrationsChartCacheSignature = signature;
    this.monthlyRegistrationsChartCache = {
      labels: monthMeta.map((item) => item.label),
      datasets: [
        {
          label: 'Cadastros',
          data: values,
          borderColor: '#1ec2bf',
          backgroundColor: 'rgba(30, 194, 191, 0.16)',
          pointBackgroundColor: '#1ec2bf',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 3,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Tendência',
          data: trend,
          borderColor: '#f0cb59',
          backgroundColor: 'rgba(240, 203, 89, 0.06)',
          pointBackgroundColor: '#f0cb59',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3.4,
          pointHoverRadius: 4.5,
          borderWidth: 2.5,
          fill: false,
          tension: 0.35
        },
        {
          label: 'Média',
          data: monthMeta.map(() => average),
          borderColor: '#8ea2bf',
          borderDash: [6, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          fill: false,
          tension: 0
        }
      ]
    };
    return this.monthlyRegistrationsChartCache;
  }

  get monthlyAverageLabel() {
    const rows = this.getMonthlySeries();
    if (!rows.length) {
      return '0,0';
    }
    const total = rows.reduce((sum, row) => sum + Number(row.total || 0), 0);
    return (total / rows.length).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  private formatMonthLabelFromKey(monthKey: string) {
    const [year, month] = String(monthKey || '').split('-').map((part) => Number(part));
    if (!year || !month) {
      return 'mês atual';
    }
    return this.formatMonthLabel(new Date(year, month - 1, 1));
  }

  private filterDashboardAssets(rows: ControlInventoryAsset[]) {
    const type = String(this.dashboardFilters.type || 'ALL');
    const status = String(this.dashboardFilters.status || 'ALL');
    const category = String(this.dashboardFilters.category || 'ALL');
    const location = String(this.dashboardFilters.location || 'ALL');
    const createdBy = String(this.dashboardFilters.created_by || 'ALL');
    const departmentId = String(this.dashboardFilters.department_id || 'ALL');
    return rows.filter((row) => {
      if (type !== 'ALL' && String(row.type || '') !== type) return false;
      if (status !== 'ALL' && String(row.status || '') !== status) return false;
      if (category !== 'ALL' && String(row.category || '') !== category) return false;
      if (location !== 'ALL' && String(row.location_name || '') !== location) return false;
      if (createdBy !== 'ALL' && String(row.created_by || '') !== createdBy) return false;
      if (departmentId !== 'ALL' && String(row.department_id || '') !== departmentId) return false;
      return true;
    });
  }

  private buildBars(rows: Array<{ label: string; total: number }>, palette: string[]) {
    const total = rows.reduce((acc, row) => acc + Number(row.total || 0), 0) || 1;
    return rows.map((row, index) => ({
      label: row.label,
      count: Number(row.total || 0),
      percent: Number(((Number(row.total || 0) / total) * 100).toFixed(1)),
      color: palette[index % palette.length]
    }));
  }

  private normalizedDashboardFilters() {
    return {
      type: this.dashboardFilters.type,
      status: this.dashboardFilters.status,
      category: this.dashboardFilters.category,
      location: this.dashboardFilters.location,
      created_by: this.dashboardFilters.created_by,
      department_id: this.dashboardFilters.department_id
    };
  }
}
